import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FiTrendingUp, FiLock, FiArrowLeft, FiCheckCircle, FiShield } from 'react-icons/fi';
import { useApi } from '../../hooks/useApi';
import { useAuthStore } from '../../stores';
import { ErrorDisplay } from '../Common/ErrorDisplay';
import { SimpleFormField } from '../Common/FormField';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { resetToken } = useParams<{ resetToken: string }>();
  const { setUser, setToken } = useAuthStore();
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);

  const resetPasswordMutation = useApi.useResetPassword({
    onSuccess: (data) => {
      if (data.token && data.user) {
        setToken(data.token);
        setUser(data.user);
        setSuccess(true);
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!password) {
      setError('Password is required');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!resetToken) {
      setError('Invalid reset token');
      return;
    }

    resetPasswordMutation.mutate({ resetToken, password });
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-primary-100 to-yellow-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center">
              <div className="bg-green-100 dark:bg-green-900/20 p-4 rounded-full">
                <FiCheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <h2 className="mt-6 text-4xl font-extrabold text-black">Password Reset Successful</h2>
            <p className="mt-2 text-lg text-gray-600">Your password has been reset successfully</p>
            <p className="mt-1 text-sm text-gray-500">Redirecting to dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-primary-100 to-yellow-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="bg-primary-500 p-4 rounded-2xl shadow-lg">
              <FiTrendingUp className="h-12 w-12 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-4xl font-extrabold text-black">Trade Pilot</h2>
          <p className="mt-2 text-lg text-gray-600">Reset Your Password</p>
          <p className="mt-1 text-sm text-gray-500">Enter your new password below</p>
        </div>

        <div className="card">
          {error && <ErrorDisplay error={error} onDismiss={() => setError('')} className="mb-4" />}

          <form onSubmit={handleSubmit} className="space-y-6">
            <SimpleFormField
              label="New Password"
              name="password"
              type="password"
              icon={<FiLock className="h-5 w-5 text-gray-400" />}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter new password"
              showPasswordToggle={true}
            />

            <SimpleFormField
              label="Confirm New Password"
              name="confirmPassword"
              type="password"
              icon={<FiShield className="h-5 w-5 text-gray-400" />}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm new password"
              showPasswordToggle={true}
            />

            <button
              type="submit"
              disabled={resetPasswordMutation.isPending}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {resetPasswordMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                  <span>Resetting Password...</span>
                </>
              ) : (
                <>
                  <FiLock className="h-5 w-5" />
                  <span>Reset Password</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <FiArrowLeft className="h-4 w-4" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;

